import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  EmptyState,
  Input,
  LoadingState,
  SectionHeader,
  Select,
  StatusChip,
  Tabs,
  Textarea,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { AppealsQueuePage, CasesQueuePage, EnforcementsQueuePage } from "../pages/TrustSafetyQueuePages";
import {
  createDeskApi,
  REASONS,
  SUBJECT_LABEL,
  type BlocklistTerm,
  type DeskApi,
  type DeskReport,
  type Overview,
  type Takedown,
} from "./deskApi";
import { fmtDate, Kpi, personName, PersonChip, priorityTone, SlaChip, timeAgo } from "./DeskShared";
import styles from "./desk.module.css";
import { CommunityRemovalsTab } from "./CommunityRemovalsTab";

const TABS = ["overview", "queue", "takedowns", "community", "cases", "enforcements", "appeals", "blocklist"] as const;
type TabId = (typeof TABS)[number];

/**
 * Trust & Safety — one page, tabs across the top, built around the job: keep
 * the platform safe. Overview says where attention is needed; Queue is where
 * reports are decided; Takedowns is every removal (restore / purge); Cases,
 * Enforcements and Appeals are the governed workflow behind high-impact
 * decisions; Blocklist flags new public content into the queue.
 */
export function TrustSafetyDeskPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab") ?? "overview";
  const tab: TabId = (TABS as readonly string[]).includes(raw) ? (raw as TabId) : "overview";
  const client = useApiClient();
  const api = useMemo(() => createDeskApi(client), [client]);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    try {
      const r = await api.overview();
      setOverview(r.overview);
      setOverviewError(null);
    } catch (e) {
      setOverviewError(apiErrorMessage(e));
    }
  }, [api]);
  useEffect(() => void loadOverview(), [loadOverview]);
  useLiveRefresh(() => void loadOverview(), { intervalMs: 30_000 });

  const go = (id: string, extra?: Record<string, string>) => {
    const next = new URLSearchParams({ tab: id, ...(extra ?? {}) });
    setParams(next);
  };

  const count = (n: number | undefined) => (n ? ` (${n})` : "");
  const items = [
    { id: "overview", label: "Overview", content: <OverviewTab overview={overview} error={overviewError} go={go} /> },
    { id: "queue", label: `Report queue${count(overview?.open_reports)}`, content: <QueueTab api={api} onChanged={loadOverview} /> },
    { id: "takedowns", label: `Takedowns${count(overview?.takedowns_active)}`, content: <TakedownsTab api={api} onChanged={loadOverview} /> },
    { id: "community", label: "Community removals", content: <CommunityRemovalsTab api={api} /> },
    { id: "cases", label: `Cases${count(overview?.open_cases)}`, content: <CasesQueuePage /> },
    { id: "enforcements", label: `Enforcement${count(overview?.proposed_enforcements)}`, content: <EnforcementsQueuePage /> },
    { id: "appeals", label: `Appeals${count(overview?.open_appeals)}`, content: <AppealsQueuePage /> },
    { id: "blocklist", label: "Blocklist", content: <BlocklistTab api={api} /> },
  ];

  return (
    <Stack gap={16}>
      <Crumbs />
      <SectionHeader
        title="Trust & Safety"
        description="Reports from Play land here. Review the content in context, decide, and the people involved are told in Play."
      />
      <Tabs items={items} value={tab} onValueChange={(id) => go(id)} />
    </Stack>
  );
}

/** Breadcrumbs set once for the page; the embedded workflow pages set their own and this re-asserts the hub. */
function Crumbs() {
  usePageBreadcrumbs([{ label: "HUDDL HQ", to: "/app" }, { label: "Trust & Safety" }]);
  return null;
}

// ── Overview ────────────────────────────────────────────────────────────────
function OverviewTab({ overview, error, go }: { overview: Overview | null; error: string | null; go: (tab: string, extra?: Record<string, string>) => void }) {
  if (error) return <Alert tone="danger" title="Unable to load overview">{error}</Alert>;
  if (!overview) return <LoadingState message="Loading overview…" />;
  const o = overview;
  const reasonRows = Object.entries(o.reasons_last_30d).sort((a, b) => b[1] - a[1]);
  const maxReason = reasonRows[0]?.[1] ?? 1;
  const nothing = o.open_reports === 0 && o.reports_last_30d === 0 && o.open_cases === 0 && o.open_appeals === 0;
  return (
    <Stack gap={24}>
      {nothing ? (
        <EmptyState
          title="No reports yet"
          description="When a player taps Report on a post, comment, video, Replay, chat message, profile, community or booking in Play, it arrives in the Report queue with the content attached. You can also file a report yourself from any subject in the queue tab, and add blocklist terms to flag new public posts automatically."
          reason="none"
        />
      ) : null}
      <div className={styles.kpis}>
        <Kpi label="Open reports" value={o.open_reports} onClick={() => go("queue")} />
        <Kpi label="SLA breached" value={o.sla_breached} tone={o.sla_breached ? "danger" : undefined} onClick={() => go("queue")} />
        <Kpi label="Due soon" value={o.sla_due_soon} tone={o.sla_due_soon ? "warn" : undefined} onClick={() => go("queue")} />
        <Kpi label="Critical / high open" value={(o.open_by_priority.critical ?? 0) + (o.open_by_priority.urgent ?? 0) + (o.open_by_priority.high ?? 0)} tone={(o.open_by_priority.critical ?? 0) ? "danger" : undefined} onClick={() => go("queue", { priority: "critical" })} />
        <Kpi label="Escalated" value={o.escalated_reports} onClick={() => go("queue", { view: "escalated" })} />
        <Kpi label="Auto-flagged open" value={o.auto_flagged_open} onClick={() => go("queue", { source: "auto_blocklist" })} />
        <Kpi label="Bans awaiting approval" value={o.proposed_enforcements} tone={o.proposed_enforcements ? "warn" : undefined} onClick={() => go("enforcements")} />
        <Kpi label="Open appeals" value={o.open_appeals} onClick={() => go("appeals")} />
        <Kpi label="Active takedowns" value={o.takedowns_active} hint={`${o.takedowns_last_7d} in the last 7 days`} onClick={() => go("takedowns")} />
        <Kpi label="Active restrictions" value={o.active_enforcements} onClick={() => go("enforcements")} />
      </div>

      <div className={styles.twoCol}>
        <div className={styles.panel}>
          <Text role="title" as="h3">Last 30 days</Text>
          <Flex gap={24} wrap>
            <Text role="body" as="span">{o.reports_last_30d} reports</Text>
            <Text role="body" as="span">{o.actioned_last_30d} actioned</Text>
            <Text role="body" as="span">{o.dismissed_last_30d} dismissed</Text>
            <Text role="body" as="span">
              {o.avg_hours_to_resolve_30d === null ? "No resolutions yet" : `${o.avg_hours_to_resolve_30d}h average to resolve`}
            </Text>
          </Flex>
          <Text role="label" as="div">Why people report</Text>
          {reasonRows.length === 0 ? (
            <Text role="small" color="muted" as="p">No reports in the last 30 days.</Text>
          ) : (
            reasonRows.map(([code, n]) => (
              <div key={code}>
                <Flex justify="space-between"><Text role="small" as="span">{REASONS.find((r) => r.value === code)?.label ?? code}</Text><Text role="small" as="span">{n}</Text></Flex>
                <div className={styles.meter}><div className={styles.meterFill} style={{ width: `${(n / maxReason) * 100}%` }} /></div>
              </div>
            ))
          )}
        </div>
        <div className={styles.panel}>
          <Text role="title" as="h3">Repeat subjects</Text>
          <Text role="caption" color="muted" as="p">Accounts reported two or more times in 30 days.</Text>
          {o.repeat_subjects.length === 0 ? (
            <Text role="small" color="muted" as="p">None.</Text>
          ) : (
            o.repeat_subjects.map((r) => (
              <Flex key={r.user.id} justify="space-between" align="center">
                <PersonChip person={r.user} />
                <Flex gap={8}>
                  {r.restriction ? <StatusChip tone="danger">{r.restriction}</StatusChip> : null}
                  <StatusChip tone="warning">{r.reports_last_30d} reports</StatusChip>
                </Flex>
              </Flex>
            ))
          )}
          <Text role="caption" color="muted" as="p">Blocklist: {o.blocklist_active_terms} active terms, {o.blocklist_hits_total} hits all-time.</Text>
        </div>
      </div>
    </Stack>
  );
}

// ── Queue ───────────────────────────────────────────────────────────────────
function QueueTab({ api, onChanged }: { api: DeskApi; onChanged: () => void }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") ?? "open";
  const priority = params.get("priority") ?? "";
  const reason = params.get("reason_code") ?? "";
  const subjectType = params.get("subject_type") ?? "";
  const source = params.get("source") ?? "";
  const page = Number(params.get("page") ?? "0") || 0;
  const [rows, setRows] = useState<DeskReport[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fileOpen, setFileOpen] = useState(false);
  const limit = 25;

  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    next.delete("page");
    setParams(next);
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setRows(null);
    try {
      const r = await api.listReports({ view, priority, reason_code: reason, subject_type: subjectType, source, limit, offset: page * limit });
      setRows(r.reports);
      setTotal(r.total);
      setError(null);
    } catch (e) {
      if (!silent) setError(apiErrorMessage(e));
    }
  }, [api, view, priority, reason, subjectType, source, page]);
  useEffect(() => void load(), [load]);
  useLiveRefresh(() => void load(true), { intervalMs: 20_000 });

  return (
    <Stack gap={16}>
      <Flex gap={8} wrap align="flex-end">
        <Select label="Show" value={view} onChange={(e) => set("view", e.target.value)} options={[
          { value: "open", label: "Open" },
          { value: "escalated", label: "Escalated" },
          { value: "resolved", label: "Resolved" },
          { value: "all", label: "All" },
        ]} />
        <Select label="Priority" value={priority} onChange={(e) => set("priority", e.target.value)} options={[
          { value: "", label: "Any" }, { value: "critical", label: "Critical" }, { value: "high", label: "High" }, { value: "normal", label: "Normal" }, { value: "low", label: "Low" },
        ]} />
        <Select label="Reason" value={reason} onChange={(e) => set("reason_code", e.target.value)} options={[{ value: "", label: "Any" }, ...REASONS]} />
        <Select label="What" value={subjectType} onChange={(e) => set("subject_type", e.target.value)} options={[
          { value: "", label: "Anything" }, ...Object.entries(SUBJECT_LABEL).map(([value, label]) => ({ value, label })),
        ]} />
        <Select label="Source" value={source} onChange={(e) => set("source", e.target.value)} options={[
          { value: "", label: "Any" }, { value: "user", label: "Player report" }, { value: "auto_blocklist", label: "Blocklist" }, { value: "staff", label: "Staff" },
        ]} />
        <Button variant="secondary" onClick={() => void load()}>Refresh</Button>
        <Button variant="secondary" onClick={() => setFileOpen(true)}>File a report</Button>
      </Flex>
      {error ? <Alert tone="danger" title="Unable to load the queue">{error}</Alert> : null}
      {!rows && !error ? <LoadingState message="Loading reports…" /> : null}
      {rows && rows.length === 0 ? (
        <EmptyState
          title={view === "open" ? "The queue is clear" : "No reports match"}
          description={view === "open" ? "No open reports. New ones from Play appear here within seconds, highest priority first." : undefined}
          reason={view === "open" ? "complete" : "filters"}
        />
      ) : null}
      {rows && rows.length > 0 ? (
        <div className={styles.queueList}>
          {rows.map((r) => (
            <div key={r.id} className={styles.queueRow} role="button" tabIndex={0}
              onClick={() => void navigate(`/app/trust-safety/reports/${r.id}`)}
              onKeyDown={(e) => { if (e.key === "Enter") void navigate(`/app/trust-safety/reports/${r.id}`); }}>
              <Stack gap={4}>
                <StatusChip tone={priorityTone(r.priority)}>{r.priority}</StatusChip>
                <SlaChip state={r.sla_state} dueAt={r.sla_due_at} />
              </Stack>
              <Stack gap={4}>
                <Text role="bodyStrong" as="div" className={styles.clip}>
                  {SUBJECT_LABEL[r.subject_type] ?? r.subject_type} · {r.reason_label ?? "No reason given"}
                  {r.source === "auto_blocklist" ? " · auto-flag" : r.source === "staff" ? " · staff" : ""}
                </Text>
                <Text role="small" color="secondary" as="div" className={styles.clip}>
                  {r.subject_preview.removed ? "[removed] " : ""}{r.subject_preview.text ?? (r.subject_preview.has_image ? "(photo)" : "—")}
                </Text>
                {r.evidence_reference ? <Text role="caption" color="muted" as="div" className={styles.clip}>“{r.evidence_reference}”</Text> : null}
              </Stack>
              <Stack gap={4}>
                <Text role="caption" color="muted" as="span">Reported party</Text>
                <Text role="small" as="span" className={styles.clip}>{r.reported_user ? personName(r.reported_user) : "—"}</Text>
                {r.prior_reports_against_user > 0 ? <StatusChip tone="warning">{r.prior_reports_against_user} other reports</StatusChip> : null}
              </Stack>
              <Stack gap={4}>
                <Text role="small" as="span">{timeAgo(r.submitted_at)}</Text>
                {r.reports_on_same_subject > 1 ? <Text role="caption" color="secondary" as="span">{r.reports_on_same_subject} reports on this</Text> : null}
                {r.resolution ? <StatusChip tone={r.resolution === "actioned" ? "success" : r.resolution === "escalated" ? "warning" : "neutral"}>{r.resolution}</StatusChip> : null}
              </Stack>
            </div>
          ))}
        </div>
      ) : null}
      {rows && total > limit ? (
        <Flex gap={8} align="center">
          <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => { const n = new URLSearchParams(params); n.set("page", String(page - 1)); setParams(n); }}>Previous</Button>
          <Text role="small" as="span">{page * limit + 1}–{Math.min(total, (page + 1) * limit)} of {total}</Text>
          <Button size="sm" variant="secondary" disabled={(page + 1) * limit >= total} onClick={() => { const n = new URLSearchParams(params); n.set("page", String(page + 1)); setParams(n); }}>Next</Button>
        </Flex>
      ) : null}
      <FileReportDialog open={fileOpen} api={api} onClose={() => setFileOpen(false)} onDone={(id) => { setFileOpen(false); onChanged(); void navigate(`/app/trust-safety/reports/${id}`); }} />
    </Stack>
  );
}

function FileReportDialog({ open, api, onClose, onDone }: { open: boolean; api: DeskApi; onClose: () => void; onDone: (id: string) => void }) {
  const [type, setType] = useState("post");
  const [id, setId] = useState("");
  const [reason, setReason] = useState("other");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setId(""); setNote(""); setError(null); } }, [open]);
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.fileReport({ subject_type: type, subject_id: id.trim(), reason_code: reason, note: note.trim() });
      onDone(r.report.id);
    } catch (e) { setError(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} title="File a report as staff" actions={
      <Flex gap={8}><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} disabled={!/^[0-9a-f-]{36}$/i.test(id.trim()) || note.trim().length < 3} onClick={() => void submit()}>File report</Button></Flex>
    }>
      <Stack gap={12}>
        <Text role="small" color="secondary" as="p">For something staff found themselves. It enters the same queue as a player report.</Text>
        <Select label="What" value={type} onChange={(e) => setType(e.target.value)} options={Object.entries(SUBJECT_LABEL).map(([value, label]) => ({ value, label }))} />
        <Input label="ID" value={id} onChange={(e) => setId(e.target.value)} placeholder="The post, comment, user… id" />
        <Select label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} options={REASONS.filter((r) => r.value !== "blocklist")} />
        <Textarea label="What you found" value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

// ── Takedowns ───────────────────────────────────────────────────────────────
function TakedownsTab({ api, onChanged }: { api: DeskApi; onChanged: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [view, setView] = useState("active");
  const [rows, setRows] = useState<Takedown[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<{ row: Takedown; kind: "restore" | "purge" } | null>(null);
  const [reason, setReason] = useState("");
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try { const r = await api.listTakedowns({ view, limit: 100 }); setRows(r.takedowns); setError(null); }
    catch (e) { setError(apiErrorMessage(e)); }
  }, [api, view]);
  useEffect(() => void load(), [load]);

  const act = async () => {
    if (!acting) return;
    setBusy(true); setActionError(null);
    try {
      if (acting.kind === "restore") {
        await api.restore(acting.row.id, reason.trim());
        toast.push({ title: "Restored — visible in Play again", tone: "success" });
      } else {
        const r = await api.purgeMedia(acting.row.id, reason.trim());
        toast.push({ title: `${r.objects_queued} file(s) queued for permanent deletion`, tone: "success" });
      }
      setActing(null); setReason(""); setConfirmPurge(false);
      onChanged(); await load();
    } catch (e) { setActionError(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Stack gap={16}>
      <Flex gap={8} align="flex-end">
        <Select label="Show" value={view} onChange={(e) => setView(e.target.value)} options={[
          { value: "active", label: "Currently removed" }, { value: "restored", label: "Restored" }, { value: "all", label: "All" },
        ]} />
        <Button variant="secondary" onClick={() => void load()}>Refresh</Button>
      </Flex>
      <Text role="small" color="secondary" as="p">
        Removing content hides it from everyone in Play but keeps it, so a mistake can be undone. Deleting the media is separate and permanent: files are queued and the storage worker deletes them within about a minute.
      </Text>
      {error ? <Alert tone="danger" title="Unable to load takedowns">{error}</Alert> : null}
      {!rows && !error ? <LoadingState message="Loading takedowns…" /> : null}
      {rows && rows.length === 0 ? <EmptyState title="Nothing removed" description="Content removed from a report decision appears here." reason="none" /> : null}
      {rows && rows.length > 0 ? (
        <div className={styles.queueList}>
          {rows.map((r) => (
            <div key={r.id} className={styles.queueRow} style={{ cursor: "default" }}>
              <Stack gap={4}>
                <StatusChip tone="info">{SUBJECT_LABEL[r.subject_type] ?? r.subject_type}</StatusChip>
                {r.restored_at ? <StatusChip tone="success">Restored</StatusChip> : <StatusChip tone="danger">Removed</StatusChip>}
                {r.media_purged_at ? <StatusChip tone="neutral">Media deleted</StatusChip> : null}
              </Stack>
              <Stack gap={4}>
                <Text role="bodyStrong" as="div" className={styles.clip}>{r.reason_label ?? r.reason_code}</Text>
                <Text role="small" color="secondary" as="div" className={styles.clip}>{r.preview?.text ?? (r.preview?.has_media ? "(media)" : "—")}</Text>
                {r.restore_reason ? <Text role="caption" color="muted" as="div">Restored: {r.restore_reason}</Text> : null}
              </Stack>
              <Stack gap={4}>
                <Text role="caption" color="muted" as="span">Owner</Text>
                <Text role="small" as="span" className={styles.clip}>{personName(r.owner)}</Text>
                <Text role="caption" color="muted" as="span">by {personName(r.removed_by)} · {fmtDate(r.removed_at)}</Text>
              </Stack>
              <Flex gap={4} wrap>
                {r.report_id ? <Button size="sm" variant="ghost" onClick={() => void navigate(`/app/trust-safety/reports/${r.report_id}`)}>Report</Button> : null}
                {!r.restored_at && !r.media_purged_at ? <Button size="sm" variant="secondary" onClick={() => { setActing({ row: r, kind: "restore" }); setReason(""); setActionError(null); }}>Restore</Button> : null}
                {r.has_purgeable_media ? <Button size="sm" variant="danger" onClick={() => { setActing({ row: r, kind: "purge" }); setReason(""); setConfirmPurge(false); setActionError(null); }}>Delete media</Button> : null}
              </Flex>
            </div>
          ))}
        </div>
      ) : null}
      <Dialog open={!!acting} onClose={() => setActing(null)} title={acting?.kind === "purge" ? "Permanently delete the media?" : "Restore this content?"} actions={
        <Flex gap={8}>
          <Button variant="secondary" onClick={() => setActing(null)}>Cancel</Button>
          <Button variant={acting?.kind === "purge" ? "danger" : "primary"} loading={busy}
            disabled={reason.trim().length < 3 || (acting?.kind === "purge" && !confirmPurge)} onClick={() => void act()}>
            {acting?.kind === "purge" ? "Delete permanently" : "Restore"}
          </Button>
        </Flex>
      }>
        <Stack gap={12}>
          {acting?.kind === "purge" ? (
            <>
              <Alert tone="danger" title="This cannot be undone">
                {acting.row.media_refs.map((m) => m.label).join(", ")} will be deleted from storage. The takedown record and audit trail stay, but the content can never be restored.
              </Alert>
              <Checkbox label="I understand this is permanent" checked={confirmPurge} onChange={(e) => setConfirmPurge(e.currentTarget.checked)} />
            </>
          ) : (
            <Text role="small" as="p">It becomes visible in Play again and the owner is told. The linked enforcement is marked reversed.</Text>
          )}
          <Textarea label="Reason (kept in the audit trail)" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
          {actionError ? <Alert tone="danger">{actionError}</Alert> : null}
        </Stack>
      </Dialog>
    </Stack>
  );
}

// ── Blocklist ───────────────────────────────────────────────────────────────
function BlocklistTab({ api }: { api: DeskApi }) {
  const toast = useToast();
  const [terms, setTerms] = useState<BlocklistTerm[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [term, setTerm] = useState("");
  const [match, setMatch] = useState("word");
  const [priority, setPriority] = useState("normal");
  const [surfaces, setSurfaces] = useState<string[]>(["post", "comment", "chat_message"]);
  const [busy, setBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<BlocklistTerm | null>(null);

  const load = useCallback(async () => {
    try { const r = await api.blocklist(); setTerms(r.terms); setError(null); }
    catch (e) { setError(apiErrorMessage(e)); }
  }, [api]);
  useEffect(() => void load(), [load]);

  const add = async () => {
    setBusy(true); setAddError(null);
    try {
      await api.addTerm({ term: term.trim(), match_type: match, surfaces, priority });
      setTerm(""); toast.push({ title: "Term added", tone: "success" }); await load();
    } catch (e) { setAddError(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const toggle = (s: string) => setSurfaces((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  return (
    <Stack gap={16}>
      <Text role="small" color="secondary" as="p">
        A new post, comment or community-chat message containing an active term is sent to the Report queue as an auto-flag for a person to review. Nothing is removed automatically, and private direct messages are never scanned.
      </Text>
      <div className={styles.panel}>
        <Flex gap={8} wrap align="flex-end">
          <Input label="Term" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="word or phrase" />
          <Select label="Match" value={match} onChange={(e) => setMatch(e.target.value)} options={[{ value: "word", label: "Whole word" }, { value: "contains", label: "Anywhere in text" }]} />
          <Select label="Queue priority" value={priority} onChange={(e) => setPriority(e.target.value)} options={[{ value: "low", label: "Low" }, { value: "normal", label: "Normal" }, { value: "high", label: "High" }, { value: "critical", label: "Critical" }]} />
          <Checkbox label="Posts" checked={surfaces.includes("post")} onChange={() => toggle("post")} />
          <Checkbox label="Comments" checked={surfaces.includes("comment")} onChange={() => toggle("comment")} />
          <Checkbox label="Community chat" checked={surfaces.includes("chat_message")} onChange={() => toggle("chat_message")} />
          <Button loading={busy} disabled={term.trim().length < 2 || surfaces.length === 0} onClick={() => void add()}>Add term</Button>
        </Flex>
        {addError ? <Alert tone="danger">{addError}</Alert> : null}
      </div>
      {error ? <Alert tone="danger" title="Unable to load blocklist">{error}</Alert> : null}
      {!terms && !error ? <LoadingState message="Loading blocklist…" /> : null}
      {terms && terms.length === 0 ? <EmptyState title="No terms yet" description="Add a word or phrase above to start flagging new public content." reason="none" /> : null}
      {terms && terms.length > 0 ? (
        <div className={styles.queueList}>
          {terms.map((t) => (
            <div key={t.id} className={styles.queueRow} style={{ cursor: "default" }}>
              <StatusChip tone={t.is_active ? "success" : "neutral"}>{t.is_active ? "Active" : "Paused"}</StatusChip>
              <Stack gap={4}>
                <Text role="bodyStrong" as="div">{t.term}</Text>
                <Text role="caption" color="muted" as="div">{t.match_type === "word" ? "Whole word" : "Anywhere"} · {t.surfaces.map((s) => SUBJECT_LABEL[s] ?? s).join(", ")} · {t.priority}</Text>
              </Stack>
              <Text role="small" as="span">{t.hit_count} hits{t.last_hit_at ? ` · last ${timeAgo(t.last_hit_at)}` : ""}</Text>
              <Flex gap={4}>
                <Button size="sm" variant="secondary" onClick={() => void api.updateTerm(t.id, { is_active: !t.is_active }).then(load).catch((e) => toast.push({ title: apiErrorMessage(e), tone: "danger" }))}>{t.is_active ? "Pause" : "Resume"}</Button>
                <Button size="sm" variant="ghost" onClick={() => setRemoving(t)}>Remove</Button>
              </Flex>
            </div>
          ))}
        </div>
      ) : null}
      <Dialog open={!!removing} onClose={() => setRemoving(null)} title="Remove this term?" actions={
        <Flex gap={8}><Button variant="secondary" onClick={() => setRemoving(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => { const t = removing; setRemoving(null); if (t) void api.deleteTerm(t.id).then(load).catch((e) => toast.push({ title: apiErrorMessage(e), tone: "danger" })); }}>Remove</Button></Flex>
      }>
        <Text role="small" as="p">Reports it already raised stay in the queue.</Text>
      </Dialog>
    </Stack>
  );
}

/** `/app/trust-safety/reports` now lives in the Queue tab. */
export function ReportsQueueRedirect() {
  const navigate = useNavigate();
  useEffect(() => { void navigate("/app/trust-safety?tab=queue", { replace: true }); }, [navigate]);
  return null;
}
